/**
 * API V2 Service - Couche API pour RADAR / COMPRENDRE / AGIR / MESURER
 * Avec cache integre
 */
import axios from 'axios';
import { getCached, setCache } from './cache';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CACHE_TTL = {
  SHORT: 2 * 60 * 1000,
  MEDIUM: 5 * 60 * 1000,
  LONG: 15 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const config = error.config;
    // Retry once on timeout (cold start) — skip if already retried
    if (error.code === 'ECONNABORTED' && !config._retried) {
      config._retried = true;
      config.timeout = 90000; // 90s for cold start retry
      console.log('[API V2] Serveur en cours de réveil, nouvelle tentative...');
      window.dispatchEvent(new CustomEvent('api:coldstart'));
      return api(config);
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    if (error.response?.status === 429) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'object' ? detail.message : detail || 'Limite quotidienne atteinte.';
      window.dispatchEvent(new CustomEvent('api:quota_exceeded', { detail: { message } }));
    }
    return Promise.reject(error);
  }
);

async function fetchWithCache(key, params, fn, ttl = CACHE_TTL.DAY, force = false) {
  if (!force) {
    const cached = getCached(key, params);
    if (cached) return { data: cached, fromCache: true };
  }
  const response = await fn();
  if (response?.data) setCache(key, params, response.data, ttl);
  return response;
}

// === RADAR API ===
export const radarAPI = {
  getBriefing: (brandId, force = false) =>
    fetchWithCache(
      `v2/radar/briefing/${brandId}`, {},
      () => api.get(`/api/v2/radar/briefing/${brandId}`),
      CACHE_TTL.DAY, force
    ),

  generateBriefing: (brandId, profileType = 'organisation') =>
    api.post(`/api/v2/radar/briefing/${brandId}/generate`, null, {
      params: { profile_type: profileType },
      timeout: 120000,
    }),

  getTrends: (brandId, force = false) =>
    fetchWithCache(
      `v2/radar/trends/${brandId}`, {},
      () => api.get(`/api/v2/radar/trends/${brandId}`),
      CACHE_TTL.DAY, force
    ),

  analyzeTrends: (brandId, hoursLookback = 48) =>
    api.post(`/api/v2/radar/trends/${brandId}/analyze`, null, {
      params: { hours_lookback: hoursLookback },
      timeout: 60000,
    }),

  getAlerts: (brandId, force = false) =>
    fetchWithCache(
      `v2/radar/alerts/${brandId}`, {},
      () => api.get(`/api/v2/radar/alerts/${brandId}`),
      CACHE_TTL.DAY, force
    ),
};

// === COMPRENDRE API ===
export const comprendreAPI = {
  getDrivers: (brandId, yearMonth = null, force = false) =>
    fetchWithCache(
      `v2/comprendre/drivers/${brandId}`, { year_month: yearMonth },
      () => api.get(`/api/v2/comprendre/drivers/${brandId}`, {
        params: yearMonth ? { year_month: yearMonth } : {},
      }),
      CACHE_TTL.DAY, force
    ),

  getSentiment: (brandId, days = 7, force = false) =>
    fetchWithCache(
      `v2/comprendre/sentiment/${brandId}`, { days },
      () => api.get(`/api/v2/comprendre/sentiment/${brandId}`, { params: { days } }),
      CACHE_TTL.DAY, force
    ),

  getWhyAnalysis: (brandId, force = false) =>
    fetchWithCache(
      `v2/comprendre/why/${brandId}`, {},
      () => api.get(`/api/v2/comprendre/why/${brandId}`, { timeout: 60000 }),
      CACHE_TTL.DAY, force
    ),

  getMentions: (brandId, params = {}, force = false) =>
    fetchWithCache(
      `v2/comprendre/mentions/${brandId}/${JSON.stringify(params)}`, {},
      () => api.get(`/api/v2/comprendre/mentions/${brandId}`, { params }),
      CACHE_TTL.DAY, force
    ),

  diagnose: (brandId, force = false) =>
    fetchWithCache(
      `v2/comprendre/diagnose/${brandId}`, {},
      () => api.post(`/api/v2/comprendre/diagnose/${brandId}`, {}, {
        timeout: 90000,
        params: force ? { force: 'true' } : {},
      }),
      CACHE_TTL.DAY, force
    ),
};

// === AGIR API ===
export const agirAPI = {
  generateContent: (data) =>
    api.post('/api/v2/agir/content/generate', data, { timeout: 120000 }),

  generateCrisisResponses: (crisisId, profileType = 'organisation') =>
    api.post(`/api/v2/agir/content/crisis-responses/${crisisId}`, null, {
      params: { profile_type: profileType },
      timeout: 120000,
    }),

  generateCounterNarrative: (data) =>
    api.post('/api/v2/agir/content/counter-narrative', data, { timeout: 120000 }),

  getActionPlan: (brandId, params = {}) =>
    fetchWithCache(
      `v2/agir/plan/${brandId}`, params,
      () => api.get(`/api/v2/agir/plan/${brandId}`, { params }),
      CACHE_TTL.DAY
    ),

  updateActionStatus: (actionId, status, notes = null) =>
    api.put(`/api/v2/agir/plan/actions/${actionId}/status`, null, {
      params: { status, notes },
    }),

  recordAction: (brandId, data) =>
    api.post(`/api/v2/agir/actions/${brandId}`, data),

  getUserActions: (brandId, params = {}) =>
    api.get(`/api/v2/agir/actions/${brandId}`, { params }),

  createActionItem: (brandId, data) =>
    api.post(`/api/v2/agir/plan/${brandId}/create`, data),

  deleteActionItem: (actionId) =>
    api.delete(`/api/v2/agir/plan/actions/${actionId}`),

  // Workflow management
  getWorkflows: (brandId, params = {}) =>
    api.get(`/api/v2/agir/workflows/${brandId}`, { params }),

  createWorkflow: (brandId, data) =>
    api.post(`/api/v2/agir/workflows/${brandId}`, data),

  updateWorkflowStatus: (brandId, workflowId, status) =>
    api.put(`/api/v2/agir/workflows/${brandId}/${workflowId}/status`, { status }),

  // Press contacts CRM
  getPressContacts: (brandId, force = false) =>
    fetchWithCache(
      `v2/agir/press-contacts/${brandId}`, {},
      () => api.get(`/api/v2/agir/press-contacts/${brandId}`),
      CACHE_TTL.DAY, force
    ),

  createPressContact: (brandId, data) =>
    api.post(`/api/v2/agir/press-contacts/${brandId}`, data),

  updatePressContact: (brandId, contactId, data) =>
    api.put(`/api/v2/agir/press-contacts/${brandId}/${contactId}`, data),

  deletePressContact: (brandId, contactId) =>
    api.delete(`/api/v2/agir/press-contacts/${brandId}/${contactId}`),

  // SEO/SERP aliases (unified in Agir page)
  getSerpResults: (brandId, keyword = null) =>
    fetchWithCache(
      `v2/agir/seo/serp/${brandId}`, { keyword },
      () => api.get(`/api/v2/agir/seo/serp/${brandId}`, { params: keyword ? { keyword } : {} }),
      CACHE_TTL.DAY
    ),

  getCampaigns: (brandId, status = null) =>
    fetchWithCache(
      `v2/agir/seo/campaigns/${brandId}`, { status },
      () => api.get(`/api/v2/agir/seo/campaigns/${brandId}`, { params: status ? { status } : {} }),
      CACHE_TTL.DAY
    ),

  // Recent mentions for reply tab
  getRecentMentions: (brandId, params = {}) =>
    fetchWithCache(
      `v2/agir/mentions/${brandId}/recent`, params,
      () => api.get(`/api/v2/agir/mentions/${brandId}/recent`, { params }),
      CACHE_TTL.DAY
    ),
};

// === MESURER API ===
export const mesurerAPI = {
  getTimeline: (brandId, limit = 20, force = false) =>
    fetchWithCache(
      `v2/mesurer/timeline/${brandId}`, { limit },
      () => api.get(`/api/v2/mesurer/timeline/${brandId}`, { params: { limit } }),
      CACHE_TTL.DAY, force
    ),

  getImpactDetail: (userActionId) =>
    api.get(`/api/v2/mesurer/impact/${userActionId}`),

  triggerMeasurement: (userActionId) =>
    api.post(`/api/v2/mesurer/impact/${userActionId}/measure`),

  getScorecard: (brandId, force = false) =>
    fetchWithCache(
      `v2/mesurer/scorecard/${brandId}`, {},
      () => api.get(`/api/v2/mesurer/scorecard/${brandId}`),
      CACHE_TTL.DAY, force
    ),

  getHistory: (brandId, months = 6) =>
    fetchWithCache(
      `v2/mesurer/history/${brandId}`, { months },
      () => api.get(`/api/v2/mesurer/history/${brandId}`, { params: { months } }),
      CACHE_TTL.DAY
    ),

  getMissionsDashboard: (brandId, force = false) =>
    fetchWithCache(
      `v2/mesurer/missions-dashboard/${brandId}`, {},
      () => api.get(`/api/v2/mesurer/missions-dashboard/${brandId}`, { timeout: 90000 }),
      CACHE_TTL.DAY, force
    ),
};

// === PROFILE API ===
export const profileAPI = {
  getMe: () =>
    fetchWithCache('v2/profile/me', {}, () => api.get('/api/v2/profile/me'), CACHE_TTL.DAY),

  updateMe: (data) => api.put('/api/v2/profile/me', data),

  getPlanConfig: () =>
    fetchWithCache('v2/profile/plan-config', {}, () => api.get('/api/v2/profile/plan-config'), CACHE_TTL.DAY),
};

export default api;
